import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, Building2, Phone, Shield, Sparkles, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSendOTP, useVerifyOTP } from "@/lib/api";
import { apiClient } from "@/lib/api/client";

const OTP_RESEND_COUNTDOWN = 30;

const Login = () => {
  const [step, setStep] = useState<"mobile" | "otp">("mobile");
  const [companyId, setCompanyId] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [rememberMe, setRememberMe] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [storedOtpCode, setStoredOtpCode] = useState<string | null>(null);
  const [storedToken, setStoredToken] = useState<string | null>(null);
  const [storedMessage, setStoredMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const sendOTPMutation = useSendOTP();
  const verifyOTPMutation = useVerifyOTP();

  const handleSendOTP = async () => {
    if (!companyId) {
      toast.error("Please enter Company ID");
      return;
    }
    if (mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      const firmId = parseInt(companyId, 10);
      if (isNaN(firmId)) {
        toast.error("Please enter a valid Company ID (numeric)");
        return;
      }

      const response = await sendOTPMutation.mutateAsync({
        mobileNo: mobile,
        firmId: firmId,
        mode: "login",
      });

      if (response.success) {
        // Store the OTP code, token, and message from API response
        setStoredOtpCode(response.otpCode || null);
        setStoredToken(response.token || null);
        setStoredMessage(response.message || null);
        setOtp(["", "", "", "", "", ""]);
        setStep("otp");
        setCountdown(OTP_RESEND_COUNTDOWN);
      }
    } catch (error: any) {
      console.error("Send OTP error:", error);
    }
  };

  const requestOTP = async () => {
    if (countdown > 0) return;

    if (!companyId) {
      toast.error("Please enter Company ID");
      return;
    }
    if (mobile.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    try {
      const firmId = parseInt(companyId, 10);
      if (isNaN(firmId)) {
        toast.error("Please enter a valid Company ID (numeric)");
        return;
      }

      const response = await sendOTPMutation.mutateAsync({
        mobileNo: mobile,
        firmId: firmId,
        mode: "login",
      });

      if (response.success) {
        // Store the OTP code, token, and message from API response
        setStoredOtpCode(response.otpCode || null);
        setStoredToken(response.token || null);
        setStoredMessage(response.message || null);
        setOtp(["", "", "", "", "", ""]);
        setCountdown(OTP_RESEND_COUNTDOWN);
        toast.success("OTP resent successfully");
      }
    } catch (error: any) {
      console.error("Resend OTP error:", error);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const handleVerifyOTP = async (overrideOtp?: string) => {
    const otpString = overrideOtp ?? otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    // Check if we have stored OTP code
    if (!storedOtpCode) {
      toast.error("OTP code not found. Please request a new OTP.");
      return;
    }

    // Compare entered OTP with stored OTP code from API response
    if (otpString === storedOtpCode) {
      // OTP matches - login successful
      // Token and user data are already stored by authService.sendOTP
      // Just ensure token is set (in case it wasn't set properly)
      if (storedToken) {
        apiClient.setToken(storedToken);
      }
      
      // Show the message from API response
      toast.success(storedMessage || "Login successful!");
      navigate("/dashboard");
    } else {
      // OTP doesn't match
      toast.error("Invalid or incorrect OTP");
      // Clear OTP input for retry
      setOtp(["", "", "", "", "", ""]);
      // Focus on first OTP input
      setTimeout(() => {
        const firstInput = document.getElementById("otp-0");
        firstInput?.focus();
      }, 0);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const numericValue = value.replace(/\D/g, "");
    if (numericValue.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);

    if (numericValue && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    } else if (numericValue && index === 5) {
      // When the last digit is entered and all 6 digits are filled, auto verify using the latest value
      const otpString = newOtp.join("");
      if (otpString.length === 6) {
        void handleVerifyOTP(otpString);
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-primary/20 to-accent/10 blur-[100px] animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-gradient-to-br from-accent/15 to-primary/10 blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-[40%] right-[30%] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-primary/10 to-accent/5 blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary) / 0.15) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative items-center justify-center p-8">
        <div className="relative z-10 max-w-xl">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3 mb-8">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-primary to-accent rounded-2xl blur-xl opacity-40" />
              <div className="relative h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-xl shadow-primary/20">
                <img src="/favicon.svg" alt="PharmaSoft" className="h-16 w-16" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight">PharmaSoft</h1>
              <p className="text-primary text-sm font-medium">Director Dashboard</p>
            </div>
          </div>

          {/* Hero Text */}
          <h2 className="text-4xl xl:text-5xl font-bold text-foreground leading-snug mb-4">
            Business Intelligence
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary pb-2">
              at Your Fingertips
            </span>
          </h2>
          
          <p className="text-muted-foreground text-base leading-relaxed mb-4">
            Access real-time analytics, track orders, manage outstanding payments, and monitor your pharmaceutical business with powerful insights.
          </p>

          {/* Feature List */}
          <div className="space-y-3">
            {[
              { icon: Sparkles, text: "Real-time sales and order tracking" },
              { icon: Shield, text: "Secure OTP-based authentication" },
              { icon: CheckCircle2, text: "Complete business overview" },
            ].map((feature, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 text-foreground/80"
                style={{ animationDelay: `${idx * 150}ms` }}
              >
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Decorative Pills */}
          <div className="absolute -right-20 top-1/4 flex flex-col gap-2 opacity-60">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i}
                className="h-1.5 rounded-full bg-gradient-to-r from-primary/40 to-transparent"
                style={{ width: `${60 - i * 10}px`, animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-[45%] flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
              <img src="/favicon.svg" alt="PharmaSoft" className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">PharmaSoft</h1>
              <p className="text-primary text-xs">Director Dashboard</p>
            </div>
          </div>

          {/* Form Card */}
          <div className="relative">
            {/* Card Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-3xl blur-xl opacity-50" />
            
            {/* Glass Card */}
            <div className="relative bg-card/95 backdrop-blur-2xl rounded-2xl border border-border shadow-xl p-6">
              {/* Step Indicator */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  step === "mobile" 
                    ? "bg-primary/10 text-primary border border-primary/30" 
                    : "bg-muted text-muted-foreground"
                }`}>
                  <Phone className="h-3 w-3" />
                  <span>Mobile</span>
                </div>
                <div className="h-px w-6 bg-border" />
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  step === "otp" 
                    ? "bg-primary/10 text-primary border border-primary/30" 
                    : "bg-muted text-muted-foreground"
                }`}>
                  <Lock className="h-3 w-3" />
                  <span>OTP</span>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-foreground mb-1">
                  {step === "mobile" ? "Welcome Back" : "Verify OTP"}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {step === "mobile"
                    ? "Sign in to access your director dashboard"
                    : `Enter the 6-digit code sent to +91 ${mobile}`}
                </p>
              </div>

              {step === "mobile" ? (
                <div className="space-y-4">
                  {/* Company ID Input */}
                  <div className="space-y-1.5">
                    <label htmlFor="companyId" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      Company ID
                    </label>
                    <div className="relative group">
                      <Input
                        id="companyId"
                        type="text"
                        placeholder="Enter your company ID"
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground rounded-xl focus:border-primary focus:ring-primary/20 transition-all"
                        autoFocus
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-focus-within:opacity-100 -z-10 blur-xl transition-opacity" />
                    </div>
                  </div>

                  {/* Mobile Number Input */}
                  <div className="space-y-1.5">
                    <label htmlFor="mobile" className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                      Mobile Number
                    </label>
                    <div className="relative group">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">+91</div>
                      <Input
                        id="mobile"
                        type="tel"
                        placeholder="Enter 10-digit number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                        maxLength={10}
                        className="h-11 bg-background border-input text-foreground placeholder:text-muted-foreground rounded-xl pl-12 focus:border-primary focus:ring-primary/20 transition-all"
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-focus-within:opacity-100 -z-10 blur-xl transition-opacity" />
                    </div>
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember" 
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                      className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary h-4 w-4"
                    />
                    <label
                      htmlFor="remember"
                      className="text-sm text-muted-foreground cursor-pointer"
                    >
                      Remember this device
                    </label>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    onClick={handleSendOTP} 
                    className="w-full h-11 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-primary-foreground font-semibold rounded-xl shadow-lg shadow-primary/25 transition-all hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98]" 
                    disabled={sendOTPMutation.isPending}
                  >
                    {sendOTPMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Sending OTP...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>Continue</span>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* OTP Input */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground text-center block">
                      Enter verification code
                    </label>
                    <div className="flex justify-center gap-2">
                      {otp.map((digit, index) => (
                        <Input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onFocus={(e) => e.target.select()}
                          className="h-12 w-11 text-center text-lg font-bold bg-background border-input text-foreground rounded-xl focus:border-primary focus:ring-primary/30 transition-all"
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Action Links */}
                  <div className="flex items-center justify-between text-sm">
                    <button
                      onClick={() => {
                        setStep("mobile");
                        setStoredOtpCode(null);
                        setStoredToken(null);
                        setStoredMessage(null);
                      }}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      ← Change number
                    </button>
                    <button
                      onClick={requestOTP}
                      disabled={countdown > 0 || sendOTPMutation.isPending}
                      className={`transition-colors ${
                        countdown > 0 
                          ? "text-muted-foreground/60 cursor-not-allowed" 
                          : "text-primary hover:text-primary/80"
                      }`}
                    >
                      {countdown > 0
                        ? `Resend in ${countdown}s`
                        : sendOTPMutation.isPending
                        ? "Sending..."
                        : "Resend code"}
                    </button>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-5 pt-4 border-t border-border">
                <p className="text-center text-xs text-muted-foreground">
                  By continuing, you agree to our{" "}
                  <span className="text-foreground hover:text-primary cursor-pointer transition-colors">Terms</span>
                  {" "}and{" "}
                  <span className="text-foreground hover:text-primary cursor-pointer transition-colors">Privacy Policy</span>
                </p>
              </div>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 mt-4 text-muted-foreground text-xs">
            <Shield className="h-3.5 w-3.5" />
            <span>256-bit SSL encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
